import React, { useRef, useLayoutEffect, useState, useMemo } from "react";
import { Clock, Hourglass, Bell, CloudSun, Eye, Timer, Flame } from "lucide-react";
import { useZmanimData } from '../useZmanimData';

const MIN_PIXELS_PER_MINUTE = 0.3;

const C = {
  bg:          "#0d1b2a",
  bgCard:      "#1a2f42",
  bgHeader:    "#0a1520",
  border:      "#2d4a63",
  borderAccent:"#f0c060",
  text:        "#f0f4f8",
  textMuted:   "#8fafc9",
  gold:        "#f0c060",
  goldDark:    "#c9922a",
  navy:        "#1e3a5f",
  emergency:   "#ff6b6b",
  axisLine:    "#1e3f5a",
};

// --- Sub-components ---

const FlexibleSpacer = ({ minutes }) => (
  <div style={{ flexGrow: minutes, minHeight: `${Math.max(0, minutes * MIN_PIXELS_PER_MINUTE)}px`, flexShrink: 0, flexBasis: '0px' }} />
);

const SimpleItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.5 : 1, padding: '7px 0', display: 'flex', gap: '12px', alignItems: 'center', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
        <div style={{
          backgroundColor: isCurrentTime ? `${C.gold}20` : `${C.axisLine}`,
          borderRadius: '50%',
          padding: '8px',
          border: isCurrentTime ? `2px solid ${C.gold}` : `1px solid ${C.border}`,
          boxShadow: isCurrentTime ? `0 0 8px ${C.gold}50` : 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <IconComponent style={{ color: isCurrentTime ? C.gold : (isPast ? C.textMuted : C.gold), width: '40px', height: '40px' }} />
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 12px 0 48px', gap: '8px', minWidth: 0 }}>
        <span style={{ color: isCurrentTime ? C.gold : (isPast ? C.textMuted : C.text), fontSize: '1em', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.title}</span>
        {item.subtitle && <span style={{ color: C.textMuted, fontSize: '0.8em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.subtitle}</span>}
        <span style={{ color: isCurrentTime ? C.gold : C.textMuted, fontSize: '1em', fontWeight: 700, flexShrink: 0 }}>{item.time}</span>
      </div>
    </div>
  );
};

const AnnotationItem = ({ item, isPast }) => {
  const pills = item.items || [{ value: item.title }];
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.5 : 1, display: 'flex', gap: '12px', alignItems: 'flex-start', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '6px' }}>
        <div style={{
          width: '10px', height: '10px', borderRadius: '50%',
          backgroundColor: C.gold,
          border: `2px solid ${C.bg}`,
          boxShadow: `0 0 4px ${C.gold}40`,
          zIndex: 10,
        }} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '4px', padding: '2px 0' }}>
        {pills.map((pill, idx) => (
          <span key={idx} style={{
            display: 'inline-flex', alignItems: 'center',
            backgroundColor: `${C.gold}10`,
            border: `1px solid ${C.border}`,
            borderRadius: '10px',
            padding: '1px 8px',
            fontSize: '0.72em', fontWeight: 500, color: C.text,
            lineHeight: 1.3, maxWidth: '100%',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {pill.value || pill.label}
          </span>
        ))}
      </div>
    </div>
  );
};

const HighlightedItem = ({ item, isCurrentTime, isPast }) => {
  const IconComponent = item.icon;
  const accentColor = item.accentColor || C.gold;
  return (
    <div style={{ direction: 'rtl', opacity: isPast ? 0.55 : 1, padding: '8px 0', display: 'flex', gap: '12px', flexShrink: 0 }}>
      <div style={{ width: '44px', flexShrink: 0, display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
        <div style={{
          width: '56px', height: '56px', borderRadius: '50%',
          backgroundColor: `${accentColor}18`,
          border: isCurrentTime ? `2px solid ${C.emergency}` : `2px solid ${accentColor}60`,
          boxShadow: isCurrentTime ? `0 0 12px ${C.emergency}40` : `0 0 8px ${accentColor}20`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: isCurrentTime ? C.emergency : accentColor,
        }}>
          <IconComponent size={28} />
        </div>
      </div>
      <div style={{ flex: 1, marginLeft: '40px', marginRight: '40px' }}>
        <div style={{
          backgroundColor: `${accentColor}12`,
          border: isCurrentTime ? `2px solid ${C.emergency}` : `1px solid ${accentColor}40`,
          borderRadius: '28px',
          padding: '12px 22px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: isCurrentTime ? `0 4px 16px ${C.emergency}25` : `0 2px 8px rgba(0,0,0,0.3)`,
        }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: '4px', height: '100%', backgroundColor: isCurrentTime ? C.emergency : accentColor, borderRadius: '0 28px 28px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: item.subtitle ? '4px' : 0, gap: '8px', minWidth: 0 }}>
            <span style={{ color: C.text, fontSize: '1.1em', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{item.title}</span>
            <span style={{
              backgroundColor: `${accentColor}18`, color: C.text,
              border: `1px solid ${accentColor}50`,
              borderRadius: '20px', padding: '2px 16px',
              fontSize: '1em', fontWeight: 700, flexShrink: 0,
            }}>{item.time}</span>
          </div>
          {item.subtitle && <span style={{ color: C.textMuted, fontSize: '0.8em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', display: 'block' }}>{item.subtitle}</span>}
        </div>
      </div>
    </div>
  );
};

const NowIndicator = ({ currentTimeStr }) => (
  <div style={{ direction: 'rtl', display: 'flex', alignItems: 'center', flexShrink: 0, position: 'relative' }}>
    <div style={{ position: 'absolute', borderTop: `2px solid ${C.gold}80`, right: '28px', left: 0, top: '50%', transform: 'translateY(-1px)', zIndex: 0 }} />
    <div style={{ width: '44px', flexShrink: 0, zIndex: 1 }} />
    <div style={{ flex: 1, display: 'flex', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
      <span style={{
        backgroundColor: C.gold,
        color: C.bg,
        fontWeight: 900,
        padding: '4px 24px',
        borderRadius: '999px',
        fontSize: '1.8em',
        lineHeight: '1.2',
        minWidth: '180px',
        textAlign: 'center',
        display: 'inline-block',
        boxShadow: `0 0 20px ${C.gold}60, 0 4px 12px rgba(0,0,0,0.4)`,
        border: `2px solid ${C.goldDark}`,
      }}>{currentTimeStr}</span>
    </div>
  </div>
);

// --- Main Component ---

const Dark = ({ zmanimMap = {}, customScheduleItems = [], title = 'סדר היום', showTitle = true, hebrewDate = null, inyaneiItems = null, inyaneiDisplayMode = null }) => {
  const [layoutState, setLayoutState] = useState({ scale: 1, heightPercent: 100 });
  const containerRef = useRef(null);
  const innerListRef = useRef(null);

  const darkIconMap = useMemo(() => ({
    misheyakir:               Eye,
    misheyakirMachmir:        Eye,
    sofZmanShmaMGA:           Hourglass,
    sofZmanShmaMGA16Point1:   Hourglass,
    sofZmanShma:              Hourglass,
    sofZmanTfillaMGA:         Hourglass,
    sofZmanTfillaMGA16Point1: Hourglass,
    sofZmanTfilla:            Hourglass,
    minchaGedola:             Bell,
    minchaGedolaMGA:          Bell,
    minchaKetana:             Bell,
    minchaKetanaMGA:          Bell,
    plagHaMincha:             Timer,
    sofZmanAchilatChametz:    Flame,
    sofZmanBiurChametz:       Flame,
    sofZmanAchilatChametzMGA: Flame,
    sofZmanBiurChametzMGA:    Flame,
    beinHaShmashos:           CloudSun,
  }), []);

  const { processedItems, nowItemIndex, currentTimeStr, currentItemMatch, currentTotalMinutes, dayWithHebrewDate } = useZmanimData({
    zmanimMap, customScheduleItems, hebrewDate,
    prayerColor: C.gold,
    lessonColor: C.goldDark,
    iconMap: darkIconMap,
  });

  useLayoutEffect(() => {
    const calculateLayout = () => {
      if (!containerRef.current || !innerListRef.current) return;
      const containerHeight = containerRef.current.clientHeight;
      const el = innerListRef.current;
      el.style.width = '100%';
      el.style.height = 'auto';
      const contentHeight = el.scrollHeight;
      let newScale = 1;
      let newHeightPercent = 100;
      if (contentHeight > containerHeight) {
        newScale = containerHeight / contentHeight;
        el.style.width = `${100 / newScale}%`;
        const newContentHeight = el.scrollHeight;
        if (newContentHeight < contentHeight) {
          newScale = Math.min(1, containerHeight / newContentHeight);
        }
        newHeightPercent = 100 / newScale;
      }
      setLayoutState(prev => {
        if (Math.abs(prev.scale - newScale) < 0.001 && Math.abs(prev.heightPercent - newHeightPercent) < 0.1) {
          el.style.width = `${100 / prev.scale}%`;
          return prev;
        }
        return { scale: newScale, heightPercent: newHeightPercent };
      });
      el.style.width = `${100 / newScale}%`;
    };
    calculateLayout();
    const observer = new ResizeObserver(() => window.requestAnimationFrame(calculateLayout));
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [processedItems]);

  const showInyaneiSidebar = (inyaneiDisplayMode === 'sidebar' || inyaneiDisplayMode === 'combined') && inyaneiItems?.length > 0;
  const showInyaneiSection = inyaneiDisplayMode === 'section' && inyaneiItems?.length > 0;

  const gridRows = [];
  if (showTitle) gridRows.push('auto');
  if (showInyaneiSection) gridRows.push('auto');
  gridRows.push('1fr');

  return (
    <div style={{
      direction: 'rtl', width: '100%', height: '100%',
      backgroundColor: C.bg,
      overflow: 'hidden',
      display: 'grid',
      gridTemplateRows: gridRows.join(' '),
      gridTemplateColumns: showInyaneiSidebar ? '18% 1fr' : '1fr',
      fontSize: 'inherit',
    }}>

      {/* Header */}
      {showTitle && (
        <div style={{
          gridColumn: '1 / -1',
          backgroundColor: C.bgHeader,
          borderBottom: `1px solid ${C.borderAccent}50`,
          padding: '0.75rem 1.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 20,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: C.gold, fontWeight: 700, fontSize: '20px' }}>
            <Clock size={20} />
            <span>{title}</span>
          </div>
          <span style={{
            backgroundColor: `${C.bgCard}`,
            border: `1px solid ${C.borderAccent}60`,
            color: C.textMuted,
            borderRadius: '20px',
            padding: '4px 20px',
            fontSize: '18px',
            fontWeight: 600,
            minWidth: '180px',
            textAlign: 'center',
            whiteSpace: 'nowrap',
          }}>{dayWithHebrewDate}</span>
        </div>
      )}

      {/* Inyanei Section */}
      {showInyaneiSection && (
        <div style={{
          gridColumn: '1 / -1',
          borderBottom: `1px solid ${C.border}`,
          backgroundColor: C.bgHeader,
          padding: '6px 1.5rem',
          display: 'flex', flexWrap: 'wrap', gap: '5px',
          alignItems: 'center', direction: 'rtl',
        }}>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            return (
              <span key={idx} style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                backgroundColor: `${C.gold}12`,
                border: `1px solid ${C.border}`,
                borderRadius: '14px', padding: '2px 10px',
                fontSize: '0.72rem', fontWeight: 600, color: C.text,
                whiteSpace: 'nowrap',
              }}>
                <Icon size={12} style={{ color: C.gold, flexShrink: 0 }} />
                {item.value || item.label}
              </span>
            );
          })}
        </div>
      )}

      {/* Inyanei Sidebar */}
      {showInyaneiSidebar && (
        <div style={{
          overflowY: 'auto', padding: '0.75rem',
          backgroundColor: C.bgHeader,
          borderRight: `1px solid ${C.border}`,
          direction: 'rtl',
          display: 'flex', flexDirection: 'column', gap: '5px',
        }}>
          <div style={{ fontWeight: 700, color: C.gold, fontSize: '0.8rem' }}>ענייני היום</div>
          {inyaneiItems.map((item, idx) => {
            const Icon = item.IconComponent;
            const isHigh = item.priority === 'high';
            return (
              <div key={`iny-${idx}`} style={{
                display: 'flex', alignItems: 'flex-start', gap: '5px',
                backgroundColor: `${C.gold}08`,
                border: `1px solid ${isHigh ? `${C.gold}60` : C.border}`,
                borderRadius: '8px', padding: '4px 8px',
              }}>
                <Icon size={13} style={{ color: isHigh ? C.gold : C.goldDark, flexShrink: 0, marginTop: '2px' }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: 600, color: C.text, lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {item.value || item.label}
                  </div>
                  {item.detail && (
                    <div style={{ fontSize: '0.6rem', color: C.textMuted, lineHeight: 1.2, marginTop: '1px' }}>
                      {item.detail}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Main Timeline */}
      <div ref={containerRef} style={{ backgroundColor: C.bg, position: 'relative', overflow: 'hidden', height: '100%' }}>
        <div
          ref={innerListRef}
          style={{
            width: `${100 / layoutState.scale}%`,
            height: `${layoutState.heightPercent}%`,
            transform: `scale(${layoutState.scale})`,
            transformOrigin: 'top right',
            display: 'flex', flexDirection: 'column', minHeight: '100%',
            padding: '1.5rem', boxSizing: 'border-box',
          }}
        >
          {/* Axis line */}
          <div style={{ position: 'absolute', top: 0, bottom: 0, width: '1px', right: '55px', backgroundColor: C.axisLine, zIndex: 0 }} />

          {processedItems.map((item, index) => {
            const isCurrentTime = currentItemMatch?.index === item.index;
            const isNowIndicatorHere = index === nowItemIndex && !currentItemMatch;
            const isPast = item.realTime < currentTotalMinutes;
            const prevItemRealTime = index === 0 ? item.realTime : processedItems[index - 1].realTime;
            let diff = 0, diffBefore = 0, diffAfter = 0;
            if (isNowIndicatorHere) {
              diffBefore = Math.max(0, currentTotalMinutes - prevItemRealTime);
              diffAfter = Math.max(0, item.realTime - currentTotalMinutes);
            } else {
              diff = index === 0 ? 0 : Math.max(0, item.realTime - prevItemRealTime);
            }
            return (
              <div key={item.index} style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                {isNowIndicatorHere && (
                  <>
                    <FlexibleSpacer minutes={diffBefore} />
                    <NowIndicator currentTimeStr={currentTimeStr} />
                    <FlexibleSpacer minutes={diffAfter} />
                  </>
                )}
                {!isNowIndicatorHere && <FlexibleSpacer minutes={diff} />}
                {item.type === 'annotation'
                  ? <AnnotationItem item={item} isPast={isPast} />
                  : item.type === 'simple'
                    ? <SimpleItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />
                    : <HighlightedItem item={item} isCurrentTime={isCurrentTime} isPast={isPast} />}
              </div>
            );
          })}

          {nowItemIndex === processedItems.length && !currentItemMatch && (
            <>
              <div style={{ flexGrow: 1, minHeight: '20px' }} />
              <NowIndicator currentTimeStr={currentTimeStr} />
            </>
          )}
          <div style={{ minHeight: '5px', flexShrink: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default Dark;
